import type { Session } from '@kidshub/contracts';

/**
 * The signed-in session, held in memory only.
 *
 * There is no storage layer here any more, and its absence is the point. The
 * refresh token lives in an httpOnly cookie the browser sends and no script can
 * read — not this application's, and not one injected into it. What remains is
 * an access token that expires in minutes and never outlives the page.
 *
 * A reload therefore starts with no session and asks the cookie for one. That
 * costs a single request and buys a credential that cannot be stolen by reading
 * it, which for a product whose credentials control a child's device is the
 * right trade.
 */

/**
 * How early a token counts as stale.
 *
 * A request that leaves with sixty seconds of validity can still arrive expired
 * on a slow connection. Renewing early costs one extra refresh; not renewing
 * early costs a failed request the parent sees.
 */
export const DEFAULT_REFRESH_SKEW_MS = 60_000;

export interface AuthApi {
  refresh(input: Record<string, never>): Promise<{ session: Session }>;
  logout(input: Record<string, never>): Promise<unknown>;
}

export interface SessionManagerOptions {
  authApi: AuthApi;
  now: () => number;
  refreshSkewMs?: number;
}

export interface SessionManager {
  current(): Session | null;
  subscribe(listener: () => void): () => void;
  /** Adopts the session a sign-in or registration just produced. */
  adopt(session: Session): void;
  /**
   * Asks the cookie whether this browser is already signed in.
   *
   * Called once at startup. Replaces reading a persisted session: the browser
   * holds the credential, so the only way to find out is to ask.
   */
  restore(): Promise<Session | null>;
  accessToken(): Promise<string | null>;
  signOut(): Promise<void>;
}

export const isAccessTokenStale = (
  session: Session,
  nowMs: number,
  skewMs: number = DEFAULT_REFRESH_SKEW_MS,
): boolean => Date.parse(session.accessTokenExpiresAt) - skewMs <= nowMs;

export const createSessionManager = ({
  authApi,
  now,
  refreshSkewMs = DEFAULT_REFRESH_SKEW_MS,
}: SessionManagerOptions): SessionManager => {
  let session: Session | null = null;
  const listeners = new Set<() => void>();

  /**
   * The in-flight refresh, if any.
   *
   * Held so that six requests waking up on a stale token produce one refresh
   * rather than six. The backend retires a refresh token when it is used and
   * treats a second use as a leak, so parallel refreshes would not merely be
   * wasteful — they would revoke the family's whole token chain.
   */
  let refreshing: Promise<Session | null> | null = null;

  const publish = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  const store = (next: Session | null) => {
    session = next;
    publish();
  };

  const exchangeCookie = async (): Promise<Session | null> => {
    try {
      const { session: renewed } = await authApi.refresh({});
      store(renewed);
      return renewed;
    } catch {
      // The cookie is absent, spent, or was revoked. None of those are
      // recoverable here; the parent signs in again.
      store(null);
      return null;
    }
  };

  const refreshOnce = (): Promise<Session | null> => {
    refreshing ??= exchangeCookie().finally(() => {
      refreshing = null;
    });
    return refreshing;
  };

  return {
    current: () => session,

    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    adopt: (next) => {
      store(next);
    },

    restore: refreshOnce,

    accessToken: async () => {
      const held = session;

      if (held === null) {
        return null;
      }

      if (!isAccessTokenStale(held, now(), refreshSkewMs)) {
        return held.accessToken;
      }

      return (await refreshOnce())?.accessToken ?? null;
    },

    signOut: async () => {
      store(null);

      try {
        await authApi.logout({});
      } catch {
        // The local session is already gone, which is what the parent asked
        // for. A server that did not hear about it will expire the token, and
        // the cookie is cleared by the same response when it does arrive.
      }
    },
  };
};
